import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ForgotPasswordUrl,
  HomeUrl,
  SignInUrl,
  SignUpUrl,
} from "../../utils/urls";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { SignInSchema } from "../../utils/validations";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  getMeAsync,
  loginAsync,
  selectLoginLoading,
  selectUserLoading,
  selectValidate2FAloading,
  validateTwoFactorAsync,
} from "../../store/auth/authSlice";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { LoginUser } from "../../types/auth";
import { AnimatePresence, motion } from "framer-motion";
import {
  Button,
  Divider,
  Flex,
  Input,
  Loading,
  Typography,
} from "djuno-design";
import {
  SocialSigninType,
  SocialsSignInButtons,
  useSocialSignin,
} from "./SocialSignin";
import CustomLink from "../general/CustomLink";

const SignIn: React.FC<{ type?: SocialSigninType }> = ({ type }) => {
  const dispatch = useAppDispatch();
  const loginLoading = useAppSelector(selectLoginLoading);
  const validateLoading = useAppSelector(selectValidate2FAloading);
  const userLoading = useAppSelector(selectUserLoading);
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const returnUrl = query.get("return-url");

  const [needOTP, setNeedOTP] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [authProviderAccess, setAuthProviderAccess] = useState<string | null>(
    null
  );

  const hasHandledSocialSigninRef = useRef(false);
  const incomingStateRef = useRef<string | null>(null);

  const {
    socialLoading,
    handleBackFromGoogle,
    handleBackFromMicrosoft,
    handleBackFromGithub,
    setAccessToken,
  } = useSocialSignin();

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: yupResolver(SignInSchema),
  });

  const callGetMe = useCallback(() => {
    dispatch(getMeAsync({})).then((action) => {
      if (action.type === "auth/me/fulfilled") {
        const state = incomingStateRef.current;
        const stateReturnUrl = state?.split("?return-url=").filter(Boolean)[0];
        const nextLink = stateReturnUrl || returnUrl || HomeUrl;

        setAccessToken(null);
        incomingStateRef.current = null;
        navigate(nextLink);
      }
    });
  }, [dispatch, navigate, returnUrl, setAccessToken]);

  const handleLoginData = useCallback(
    (login: LoginUser, state: string | null) => {
      incomingStateRef.current = state;
      if (login.TwoFactorEnabled) {
        setNeedOTP(true);
        setAuthProviderAccess(login.ProviderAccessToken);
      } else {
        callGetMe();
      }
    },
    [callGetMe]
  );

  const onSubmit = (data: any) => {
    if (!loginLoading) {
      dispatch(loginAsync(data)).then((action) => {
        if (action.type === "auth/login/fulfilled") {
          const { login } = action.payload as { login: LoginUser };
          handleLoginData(login, null);
        }
      });
    }
  };

  const callValidateCode = useCallback(() => {
    if (typeof code === "undefined" || code === "") {
      setCodeError("Code is a required");
    } else {
      let loginType = "manual";
      let loginData = getValues() as any;
      if (authProviderAccess) {
        loginType = "social";
        loginData = {
          ProviderAccessToken: authProviderAccess,
          SigninProvider:
            type === "signin-google" ? 0 : type === "signin-microsoft" ? 1 : 2,
        };
      }

      dispatch(
        validateTwoFactorAsync({
          loginType,
          AuthenticationCode: code,
          ...loginData,
        })
      ).then((action) => {
        if (action.type === "auth/validate-2fa/fulfilled") {
          callGetMe();
        }
      });
    }
  }, [authProviderAccess, callGetMe, code, dispatch, getValues, type]);

  useEffect(() => {
    if (hasHandledSocialSigninRef.current) return;

    if (type === "signin-google") {
      hasHandledSocialSigninRef.current = true;
      handleBackFromGoogle({
        callback: handleLoginData,
      });
    }

    if (type === "signin-microsoft") {
      hasHandledSocialSigninRef.current = true;
      handleBackFromMicrosoft({
        callback: handleLoginData,
      });
    }

    if (type === "signin-github") {
      hasHandledSocialSigninRef.current = true;
      handleBackFromGithub({
        callback: handleLoginData,
      });
    }
  }, [
    handleBackFromGoogle,
    handleLoginData,
    handleBackFromMicrosoft,
    handleBackFromGithub,
    type,
  ]);

  const handleSwitchAccount = useCallback(() => {
    setNeedOTP(false);
    setCodeError(null);
    setCode("");
    incomingStateRef.current = null;
  }, []);

  return (
    <>
      <Typography.Title level={5} className="text-center">
        Sign in to your account
      </Typography.Title>
      <AnimatePresence>
        {!socialLoading && !userLoading && !needOTP && (
          <motion.form
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onSubmit={handleSubmit(onSubmit)}
            className=" mt-4"
          >
            <Flex direction="col" className="gap-1">
              <Flex direction="col" className="gap-2">
                <Input
                  {...register("Email")}
                  label="Email address"
                  required
                  placeholder="your@email.com"
                  error={errors.Email?.message}
                  containerClassName="mb-4"
                />
                <Input
                  label="Password"
                  type="password"
                  {...register("Password")}
                  required
                  placeholder="●●●●●●"
                  error={errors.Password?.message}
                />
              </Flex>
              <span>
                <Link
                  to={ForgotPasswordUrl}
                  className="text-sm text-primary-300 hover:text-primary-400"
                >
                  Forgot your password?
                </Link>
              </span>
              <Flex direction="col" className="mt-4">
                <Button
                  uiType="primary"
                  type="submit"
                  className="!w-full"
                  loading={loginLoading}
                >
                  <Typography.Text
                    uiType="transparent"
                    className="w-full text-center"
                    size="sm"
                  >
                    Sign in to your account
                  </Typography.Text>
                </Button>

                <Divider text="or" className="!my-1.5 md:!my-3" />
                <SocialsSignInButtons />

                <Flex justify="center" className="gap-1 mt-5">
                  <Typography.Text className="!text-sm">
                    Don't have an account?
                  </Typography.Text>
                  <CustomLink to={SignUpUrl} className="text-sm">
                    Sign up
                  </CustomLink>
                </Flex>
              </Flex>
            </Flex>
          </motion.form>
        )}

        {!socialLoading && !userLoading && needOTP && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="gap-1 flex flex-col mt-4"
          >
            <Flex direction="col" className="gap-5">
              <Typography.Text className="!text-sm">
                Please enter the code from your authenticator app. You can also
                enter one of your backup codes.
              </Typography.Text>
              <Input
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  setCodeError(null);
                  setCode(value);
                }}
                className="mb-4"
                label="Code"
                required
                placeholder="000000"
                error={codeError}
              />
            </Flex>
            <Flex direction="col" className="mt-1">
              <Button
                uiType="primary"
                onClick={callValidateCode}
                className="!w-full"
                loading={validateLoading}
              >
                <Typography.Text
                  uiType="transparent"
                  className="!text-sm w-full"
                >
                  Sign in
                </Typography.Text>
              </Button>
            </Flex>
            <Flex
              direction="col"
              justify="center"
              items="center"
              className="gap-1 mt-5"
            >
              <Typography.Text className="!text-sm">
                Want to use a different account?
              </Typography.Text>
              <CustomLink
                to={SignInUrl}
                onClick={handleSwitchAccount}
                className="text-sm"
              >
                Switch account
              </CustomLink>
            </Flex>
          </motion.div>
        )}

        {(userLoading || socialLoading) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Flex items="center" justify="center" className="min-h-[200px]">
              <Loading borderSize={2} />
            </Flex>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
export default SignIn;
